import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useMemo, useState } from "react";
import { notifyError } from "../../utils/notifications";
import { WkoLoadingSpinner } from "../WkoLoadingSpinner";
import { BenchmarkCard } from "./BenchmarkCard";
import { LeafletHeatmap } from "./LeafletHeatmap";
const createMapOptions = (center) => ({
    minZoom: 2,
    maxZoom: 11,
    initialZoomLevel: 7,
    attributionControl: false,
    dragging: false,
    doubleClickZoom: false,
    scrollWheelZoom: false,
    zoomControl: false,
    markerMinZoomLevel: 99,
    zoneMinZoomLevel: 99,
    showLocations: false,
    showMarkersOnlyOnHover: false,
    locationLinkTo: "",
    initialCenter: center,
    maxBounds: [
        [-90, -180],
        [90, 180],
    ],
});
export const LevelsLocationSummary = ({ options, title = "Selected location", }) => {
    const [loading, setLoading] = useState(false);
    const [ncHeatmapPoints, setNcHeatmapPoints] = useState(null);
    const [data, setData] = useState(null);
    const mapId = useId().replace(/:/g, "");
    const mapCenter = useMemo(() => {
        if (data == null)
            return null;
        return [data.Latitude, data.Longitude];
    }, [data]);
    const mapOptions = useMemo(() => {
        if (mapCenter == null)
            return null;
        return createMapOptions(mapCenter);
    }, [mapCenter]);
    const mapKey = mapCenter == null ? "location-summary-map-empty" : `${mapCenter[0]}-${mapCenter[1]}`;
    useEffect(() => {
        if (!options.isReady)
            return;
        setNcHeatmapPoints(null);
        setData(null);
        setLoading(true);
        const userHeatmapParams = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            vesselTypes: options.vesselTypes.join(","),
            fleets: options.fleet.map((f) => f.ID).join(","),
        });
        const locationSummaryparams = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            vesselTypes: options.vesselTypes.join(","),
            fleets: options.fleet.map((f) => f.ID).join(","),
            locationId: options.locationId == null ? "" : String(options.locationId),
        });
        Promise.all([
            fetch(`/api/benchmark/userheatmap?${userHeatmapParams}`)
                .then((res) => {
                if (res.ok)
                    return res.json();
                throw new Error(res.statusText);
            })
                .then((ncHeatmapPoints) => {
                setNcHeatmapPoints(ncHeatmapPoints);
            }),
            fetch(`/api/levelsbenchmark/location/summary?${locationSummaryparams}`)
                .then((res) => {
                if (res.ok)
                    return res.json();
                throw new Error(res.statusText);
            })
                .then((levelsLocationSummaryData) => {
                setData(levelsLocationSummaryData);
            }),
        ])
            .catch(() => {
            notifyError("An error has occurred when loading location summary data. Please try again.");
        })
            .finally(() => setLoading(false));
    }, [
        options.isReady,
        options.startDate,
        options.endDate,
        options.vesselTypes.join(","),
        options.fleet.map((f) => f.ID).join(","),
        options.locationId,
    ]);
    if (loading) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx(WkoLoadingSpinner, {}) }));
    }
    if (data == null) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx("div", { className: "py-5", children: _jsx("p", { className: "text-secondary text-center mb-0", children: "No data for this selection." }) }) }));
    }
    return (_jsx(_Fragment, { children: loading ? (_jsx(WkoLoadingSpinner, {})) : data == null ? (_jsxs(_Fragment, { children: [_jsx("h2", { children: "Selected location" }), _jsx("div", { className: "card shadow", children: _jsx("div", { children: _jsx("p", { className: "text-secondary text-center mb-0", children: "No data for this selection." }) }) })] })) : (_jsxs(_Fragment, { children: [_jsx("h2", { children: "Selected location" }), _jsxs("div", { className: "card shadow flex-row", children: [_jsxs("div", { className: "card-body pt-2", style: { maxWidth: "33%" }, children: [_jsx("p", { className: "h2", children: data?.Name }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "badge rounded-pill badge-l1", children: "Level 1" }), _jsxs("span", { children: [data?.L1NcDaysPerVisit, " NC/visit"] })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "badge rounded-pill badge-l2", children: "Level 2" }), _jsxs("span", { children: [data?.L2NcDaysPerVisit, " NC/visit"] })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "badge rounded-pill badge-l3", children: "Level 3" }), _jsxs("span", { children: [data?.L3NcDaysPerVisit, " NC/visit"] })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "badge rounded-pill badge-l4", children: "Level 4" }), _jsxs("span", { children: [data?.L4NcDaysPerVisit, " NC/visit"] })] })] }), mapOptions != null && ncHeatmapPoints != null ? (_jsx(LeafletHeatmap, { id: `${mapId}-location-map`, className: "flex-grow-1", style: { height: "11.371rem" }, options: mapOptions, data: ncHeatmapPoints, locations: [], params: null, onViewportChange: () => { } }, mapKey)) : (_jsx("div", { style: { height: "11.371rem" }, className: "flex-grow-1" }))] })] })) }));
};
